import React, {useState, useRef, useEffect} from 'react'

type Option<T extends string | number> = {
 id: T
 label: string
}

type Props<T extends string | number> = {
 label: string
 placeholder: string
 value: T | null
 options: Option<T>[]
 onChange: (id: T) => void
 disabled?: boolean
}

const CustomDropdown = <T extends string | number>({
 label,
 placeholder,
 value,
 options,
 onChange,
 disabled = false,
}: Props<T>) => {
 const [open, setOpen] = useState(false)
 const ref = useRef<HTMLDivElement>(null)

 useEffect(() => {
  const handler = (e: MouseEvent) => {
   if (
    ref.current &&
    !ref.current.contains(e.target as Node)
   ) {
    setOpen(false)
   }
  }
  document.addEventListener('mousedown', handler)
  return () =>
   document.removeEventListener('mousedown', handler)
 }, [])

 const selected = options.find((o) => o.id === value)

 return (
  <div ref={ref} className='relative w-full pt-2 pb-2'>
   <div
    onClick={() => {
     if (!disabled) setOpen((prev) => !prev)
    }}
    className={`relative h-[56px] rounded-lg border px-4 pt-2
          ${
           disabled
            ? 'cursor-not-allowed'
            : 'cursor-pointer'
          }
          ${selected ? 'bg-[#F5F8FA]' : 'bg-white'}
          ${open ? 'border-blue-400' : 'border-gray-200'}`}
   >
    <p className='text-xs font-medium text-[#4E83E4]'>
     {label}
     <span className='text-red'>*</span>
    </p>

    <div className='flex items-center justify-between'>
     <span
      className={`text-base ${
       selected ? 'text-[#101010]' : 'text-[#80868C]'
      }`}
     >
      {selected?.label || placeholder}
     </span>

     <span
      className={`text-xl ${
       disabled ? 'text-gray-300' : 'text-gray-600'
      }`}
     >
      <svg
       className={`h-6 w-6 text-gray-600 transition-transform ${
        open && !disabled ? 'rotate-180' : ''
       }`}
       viewBox='0 0 20 20'
       fill='none'
       stroke='currentColor'
      >
       <path d='M6 8l4 4 4-4' strokeWidth='2' />
      </svg>
     </span>
    </div>
   </div>

   {open && !disabled && (
    <div className='absolute left-0 top-[64px] z-30 w-full rounded-xl border border-gray-200 bg-white shadow-xl'>
     {options.map((opt) => (
      <label
       key={opt.id}
       className='flex cursor-pointer items-center gap-4 px-5 py-4 hover:bg-gray-50'
      >
       <input
        type='radio'
        checked={value === opt.id}
        onChange={() => {
         onChange(opt.id)
         setOpen(false)
        }}
        className=' 
       h-5 w-5
    appearance-none
    rounded-full
    border-2 border-[#4E83E4]
    relative
    focus:outline-none
    focus:ring-2
    focus:ring-[#4E83E4]

    checked:after:content
    checked:after:absolute
    checked:after:top-1/2
    checked:after:left-1/2
    checked:after:-translate-x-1/2
    checked:after:-translate-y-1/2
    checked:after:h-2.5
    checked:after:w-2.5
    checked:after:rounded-full
    checked:after:bg-[#4E83E4]'
       />
       <span className='text-base text-gray-900'>
        {opt.label}
       </span>
      </label>
     ))}
    </div>
   )}
  </div>
 )
}

export default CustomDropdown
